// Apolo IDE — SprintBuilderModal (sprint plan-tab-v1, E7).
//
// Wizard simples: preview do .md gerado + ações (copiar, baixar,
// enviar pra aba Code). Lê blocks + connections do hook e gera o
// markdown via buildSprintDirective.

import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Clipboard, Download, Gauge, Loader2, Rocket, Sparkles, Wand2, X } from 'lucide-react';
import { useAgentStore } from '../../store/useAgentStore.js';
import { buildSprintDirective, buildSprintSummary } from '../../lib/plan/buildSprintDirective.js';
import { computePhaseEstimates } from '../../lib/plan/estimates.js';
import { patchPlan } from '../../lib/plan/api.js';
import { apiFetch } from '../../lib/api.js';

const AI_MODEL = 'anthropic/claude-opus-4-7-medium';

function loadEstOverrides(planId) {
  if (!planId) return {};
  try {
    const raw = localStorage.getItem(`apolo:plan:${planId}:estimates`);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

export default function SprintBuilderModal({ open, onClose, plan, blocks, connections }) {
  const [copied, setCopied] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [estOverrides, setEstOverrides] = useState(() => loadEstOverrides(plan?.id));
  const [loadingAi, setLoadingAi] = useState(false);
  const setActiveWorkspace = useAgentStore((s) => s.setActiveWorkspace);
  const setActiveSession = useAgentStore((s) => s.setActiveSession);
  const createCodeSession = useAgentStore((s) => s.createCodeSession);

  const estimates = useMemo(() => computePhaseEstimates(blocks, estOverrides), [blocks, estOverrides]);
  const directive = useMemo(() => buildSprintDirective({ plan, blocks, connections, options: { estimates } }), [plan, blocks, connections, estimates]);
  const summary = useMemo(() => buildSprintSummary({ plan, blocks }), [plan, blocks]);

  useEffect(() => { if (open) { setCopied(false); setErr(null); setEstOverrides(loadEstOverrides(plan?.id)); } }, [open, plan?.id]);

  // Persiste um override de estimativa por fase + recomputa.
  function patchOverride(phaseId, field, value) {
    setEstOverrides((prev) => {
      const next = { ...prev, [phaseId]: { ...prev[phaseId], [field]: value } };
      try { if (plan?.id) localStorage.setItem(`apolo:plan:${plan.id}:estimates`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  // Draft de ROI + KPIs por fase via IA (opcional, sob demanda).
  async function estimateWithAi() {
    if (!estimates.phases.length) return;
    setLoadingAi(true); setErr(null);
    const phaseList = estimates.phases.map((p) => `${p.id} (${p.label}, ${p.hours}h)`).join(', ');
    const sys = [
      'Você é analista de processos do Apolo. Para cada fase de um plano de sprint, sugira um ROI curto e 1-2 KPIs concretos e mensuráveis.',
      'Responda APENAS com um array JSON dentro de ```json```, formato:',
      '[{"phase":"<id da fase>","roi":"frase curta de ROI","kpis":["KPI 1","KPI 2"]}]',
      `Fases: ${phaseList}.`,
      `Plano: "${plan?.name || 'sem nome'}".`,
    ].join('\n');
    const res = await apiFetch('/api/chat', {
      method: 'POST',
      body: { model: AI_MODEL, system: sys, messages: [{ role: 'user', content: 'Estime ROI e KPIs por fase.' }], max_tokens: 700 },
    });
    setLoadingAi(false);
    if (!res.ok) { setErr(res.error || 'Falha ao estimar com IA'); return; }
    const content = res.data?.content?.[0]?.text || res.data?.text || '';
    const fence = /```json\s*([\s\S]+?)```/.exec(content);
    try {
      const arr = JSON.parse((fence ? fence[1] : content).trim());
      if (Array.isArray(arr)) {
        const next = { ...estOverrides };
        for (const x of arr) {
          if (!x?.phase) continue;
          next[x.phase] = {
            ...next[x.phase],
            ...(x.roi ? { roi: String(x.roi) } : {}),
            ...(Array.isArray(x.kpis) ? { kpis: x.kpis.map(String) } : {}),
          };
        }
        setEstOverrides(next);
        try { if (plan?.id) localStorage.setItem(`apolo:plan:${plan.id}:estimates`, JSON.stringify(next)); } catch { /* ignore */ }
      }
    } catch { setErr('IA retornou formato inesperado'); }
  }

  useEffect(() => {
    function esc(e) { if (e.key === 'Escape' && open) onClose(); }
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [open, onClose]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(directive);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { setErr('Falha ao copiar'); }
  }

  function download() {
    const slug = (plan?.name || 'sprint').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    const blob = new Blob([directive], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug || 'sprint'}-directive.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
  }

  async function sendToCode() {
    setSending(true); setErr(null);
    const res = await createCodeSession({
      name: plan?.name || 'Sprint do Plan tab',
      description: directive.length <= 180_000 ? directive : directive.slice(0, 180_000) + '\n\n_(directive truncated)_',
    });
    setSending(false);
    if (!res.ok) { setErr(res.error || 'Falha ao criar session'); return; }
    // Marcar plano como exported (best-effort)
    if (plan?.id) patchPlan(plan.id, { status: 'exported' }).catch(() => {});
    setActiveWorkspace('code');
    if (res?.data?.session?.id || res?.session?.id) {
      setActiveSession(res.data?.session?.id || res.session?.id);
    }
    onClose();
  }

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sprint-builder-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 24, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 24, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative flex h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-apolo-plan-accentBorder bg-apolo-surface1 sm:h-[80vh] sm:max-h-[800px] sm:max-w-4xl sm:rounded-2xl"
            style={{ boxShadow: 'var(--apolo-plan-glow-wide), 0 24px 80px rgba(0,0,0,0.5)' }}
          >
            {/* Grab handle (mobile bottom-sheet affordance) */}
            <div className="flex shrink-0 justify-center pt-2 sm:hidden">
              <span aria-hidden="true" className="h-1 w-10 rounded-full bg-apolo-borderStrong" />
            </div>

            {/* Header */}
            <div className="flex shrink-0 items-start gap-3 border-b border-apolo-borderSubtle px-4 py-3 sm:items-center sm:px-5 sm:py-4">
              <span
                aria-hidden="true"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
              >
                <Rocket size={16} className="text-white" />
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <h2 id="sprint-builder-title" className="text-base font-semibold tracking-tight">Sprint Builder</h2>
                <p className="line-clamp-2 text-xs leading-snug text-apolo-textTertiary">{summary}</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Fechar"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary active:scale-95"
              >
                <X size={16} />
              </button>
            </div>

            {/* Estimativas por fase (editável) */}
            {estimates.phases.length > 0 ? (
              <div className="shrink-0 border-b border-apolo-borderSubtle bg-apolo-surface1/40 px-4 py-3 sm:px-5">
                <div className="mb-2 flex items-center gap-2">
                  <Gauge size={13} className="text-apolo-plan-accent" aria-hidden="true" />
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-apolo-textSecondary">Estimativas por fase</span>
                  <span className="text-[11px] text-apolo-textTertiary">· total {estimates.totalHours}h</span>
                  <button
                    type="button"
                    onClick={estimateWithAi}
                    disabled={loadingAi}
                    title="Sugerir ROI e KPIs por fase com IA"
                    className="ml-auto inline-flex items-center gap-1 rounded-full border border-dashed border-apolo-plan-accentBorder px-2.5 py-1 text-[11px] font-medium text-apolo-plan-accent transition hover:bg-apolo-plan-accentMuted active:scale-[0.97] disabled:opacity-60"
                  >
                    {loadingAi ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                    estimar com IA
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-[11px]">
                    <thead>
                      <tr className="text-left text-apolo-textTertiary">
                        <th className="py-1 pr-2 font-medium">Fase</th>
                        <th className="py-1 pr-2 font-medium">Tempo</th>
                        <th className="py-1 pr-2 font-medium">Complex.</th>
                        <th className="py-1 pr-2 font-medium">ROI</th>
                        <th className="py-1 font-medium">KPIs (;)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estimates.phases.map((p) => (
                        <tr key={p.id} className="border-t border-apolo-borderSubtle/50">
                          <td className="py-1 pr-2 whitespace-nowrap text-apolo-textPrimary">{p.emoji} {p.label}</td>
                          <td className="py-1 pr-2">
                            <span className="inline-flex items-center gap-1">
                              <input
                                type="number" min="0" step="0.5" value={p.hours}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === '') { patchOverride(p.id, 'hours', undefined); return; }
                                  const n = Number(raw);
                                  if (!Number.isFinite(n)) return; // ignora entrada inválida (NaN)
                                  patchOverride(p.id, 'hours', Math.max(0, n));
                                }}
                                className="w-14 rounded border border-apolo-borderSubtle bg-apolo-bg px-1.5 py-0.5 text-[11px] focus:border-apolo-plan-accentBorder focus:outline-none"
                                aria-label={`Tempo da fase ${p.label} em horas`}
                              />
                              <span className="text-apolo-textTertiary">h</span>
                            </span>
                          </td>
                          <td className="py-1 pr-2 text-apolo-textSecondary">{p.complexity}</td>
                          <td className="py-1 pr-2">
                            <input
                              type="text" value={p.roi}
                              onChange={(e) => patchOverride(p.id, 'roi', e.target.value)}
                              className="w-40 rounded border border-apolo-borderSubtle bg-apolo-bg px-1.5 py-0.5 text-[11px] focus:border-apolo-plan-accentBorder focus:outline-none"
                              aria-label={`ROI da fase ${p.label}`}
                            />
                          </td>
                          <td className="py-1">
                            <input
                              type="text" value={p.kpis.join('; ')}
                              onChange={(e) => patchOverride(p.id, 'kpis', e.target.value.split(';').map((s) => s.trim()).filter(Boolean))}
                              className="w-48 rounded border border-apolo-borderSubtle bg-apolo-bg px-1.5 py-0.5 text-[11px] focus:border-apolo-plan-accentBorder focus:outline-none"
                              aria-label={`KPIs da fase ${p.label}`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {/* Preview */}
            <div className="flex-1 overflow-y-auto bg-apolo-bg p-4 sm:p-5">
              <pre className="whitespace-pre-wrap break-words font-mono text-[11px] leading-relaxed text-apolo-textPrimary sm:text-[12px]">
                {directive}
              </pre>
            </div>

            {/* Footer actions — stack em mobile, linha em desktop */}
            <div
              className="flex shrink-0 flex-col gap-3 border-t border-apolo-borderSubtle px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-3.5"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
              <div className="flex items-center gap-1.5 text-[11px] text-apolo-textTertiary sm:text-xs">
                <Sparkles size={12} aria-hidden="true" className="shrink-0" />
                <span>{blocks?.length || 0} blocos · {directive.length.toLocaleString('pt-BR')} chars</span>
                {err ? <span className="ml-1 truncate text-apolo-error">{err}</span> : null}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md border border-apolo-borderSubtle px-3 text-xs font-medium text-apolo-textPrimary transition hover:bg-apolo-surface2 active:scale-[0.98] sm:h-9 sm:flex-none"
                  aria-label="Copiar markdown"
                >
                  {copied ? <Check size={13} /> : <Clipboard size={13} />}
                  {copied ? 'Copiado' : 'Copiar'}
                </button>
                <button
                  type="button"
                  onClick={download}
                  className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-md border border-apolo-borderSubtle px-3 text-xs font-medium text-apolo-textPrimary transition hover:bg-apolo-surface2 active:scale-[0.98] sm:h-9 sm:flex-none"
                  aria-label="Baixar arquivo markdown"
                >
                  <Download size={13} /> Baixar<span className="hidden sm:inline">&nbsp;.md</span>
                </button>
                <button
                  type="button"
                  onClick={sendToCode}
                  disabled={sending || !blocks?.length}
                  className="inline-flex h-10 flex-[1.4] items-center justify-center gap-1.5 rounded-md px-4 text-xs font-semibold text-white transition hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-plan-accentRing active:scale-[0.98] disabled:opacity-60 sm:h-9 sm:flex-none"
                  style={{ background: 'var(--apolo-plan-gradient)', boxShadow: 'var(--apolo-plan-glow-tight)' }}
                >
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <Rocket size={13} />}
                  Enviar<span className="hidden sm:inline">&nbsp;pra Code</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
